import { lazy, Suspense } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";

import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import OrgProtectedRoute from "@/components/OrgProtectedRoute";
import AppLayout from "@/components/layout/AppLayout";

// Eager: public entry pages for fast first paint
import LandingPage from "@/pages/LandingPage";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";

// Lazy: all authenticated/secondary routes
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const SetupWizard = lazy(() => import("@/pages/SetupWizard"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Oversight = lazy(() => import("@/pages/Oversight"));
const DIY = lazy(() => import("@/pages/DIY"));
const Documents = lazy(() => import("@/pages/Documents"));
const ActivityLog = lazy(() => import("@/pages/ActivityLog"));
const Resources = lazy(() => import("@/pages/Resources"));
const SettingsPage = lazy(() => import("@/pages/Settings"));
const SecuritySettings = lazy(() => import("@/pages/SecuritySettings"));
const OrgDashboard = lazy(() => import("@/pages/OrgDashboard"));
const OrgMembers = lazy(() => import("@/pages/OrgMembers"));
const OrgMemberDetail = lazy(() => import("@/pages/OrgMemberDetail"));
const OrgAssignments = lazy(() => import("@/pages/OrgAssignments"));
const OrgAccessReview = lazy(() => import("@/pages/OrgAccessReview"));
const AuditLog = lazy(() => import("@/pages/AuditLog"));
const MyCredentials = lazy(() => import("@/pages/MyCredentials"));
const MyTasks = lazy(() => import("@/pages/MyTasks"));
const Help = lazy(() => import("@/pages/Help"));
const CredentialVault = lazy(() => import("@/pages/CredentialVault"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Unsubscribe = lazy(() => import("@/pages/Unsubscribe"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="flex justify-center py-20">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />

              {/* Setup wizard (auth required, no layout) */}
              <Route path="/setup" element={<ProtectedRoute><SetupWizard /></ProtectedRoute>} />

              {/* App routes (auth + layout) */}
              <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/renewals" element={<Navigate to="/my-credentials" replace />} />
                <Route path="/oversight" element={<Oversight />} />
                <Route path="/diy" element={<DIY />} />
                <Route path="/my-credentials" element={<MyCredentials />} />
                <Route path="/my-tasks" element={<MyTasks />} />
                <Route path="/documents" element={<Documents />} />
                <Route path="/vault" element={<CredentialVault />} />
                <Route path="/log" element={<ActivityLog />} />
                <Route path="/resources" element={<Resources />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/settings/security" element={<SecuritySettings />} />
                <Route path="/audit" element={<AuditLog />} />
                <Route path="/help" element={<Help />} />

                {/* Org admin routes */}
                <Route path="/org/dashboard" element={<OrgProtectedRoute><OrgDashboard /></OrgProtectedRoute>} />
                <Route path="/org/members" element={<OrgProtectedRoute><OrgMembers /></OrgProtectedRoute>} />
                <Route path="/org/member/:userId" element={<OrgProtectedRoute><OrgMemberDetail /></OrgProtectedRoute>} />
                <Route path="/org/assignments" element={<OrgProtectedRoute><OrgAssignments /></OrgProtectedRoute>} />
                <Route path="/org/access-review" element={<OrgProtectedRoute><OrgAccessReview /></OrgProtectedRoute>} />
              </Route>

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
